// @ts-nocheck
/**
 * Subject Radar Chart Component
 * Carbon Charts implementation for subject performance visualization
 */

import React, { useMemo } from 'react';
import { RadarChart } from '@carbon/charts-react';
import { Tile, Loading, InlineNotification } from '@carbon/react';
import { ChartRadar } from '@carbon/icons-react';
import '@carbon/charts-react/styles.css';

interface SubjectPerformanceData {
  subject: string;
  score: number;
  potential: number;
  student: string;
}

interface SubjectRadarChartProps {
  data: SubjectPerformanceData[];
  height?: number;
  title?: string;
  loading?: boolean;
  error?: string;
}

const SubjectRadarChart: React.FC<SubjectRadarChartProps> = ({
  data,
  height = 350,
  title = 'Subject Performance Analysis',
  loading = false,
  error = '',
}) => {
  // Transform data for Carbon Charts format
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];

    // Group by student for multiple series
    const studentGroups: { [key: string]: SubjectPerformanceData[] } = {};
    data.forEach(item => {
      if (!studentGroups[item.student]) {
        studentGroups[item.student] = [];
      }
      studentGroups[item.student].push(item);
    });

    // Transform to Carbon Charts format
    const result: any[] = [];
    Object.keys(studentGroups).forEach(student => {
      studentGroups[student].forEach(item => {
        result.push({
          key: item.subject,
          group: student,
          value: item.score,
        });
      });
    });

    return result;
  }, [data]);

  // Carbon Charts options with Sigma08 theme colors
  const chartOptions = useMemo(
    () => ({
      title: '',
      radar: {
        axes: {
          angle: 'key',
          value: 'value',
        },
        alignment: 'center' as const,
      },
      height,
      theme: 'white',
      color: {
        scale: {
          Student: '#8a3ffc', // Galaxy Purple
          Average: '#0043ce', // Carbon Blue
          default: '#525252', // Carbon Gray
        },
      },
      data: {
        groupMapsTo: 'group',
      },
      grid: {
        enabled: true,
      },
      tooltip: {
        enabled: true,
        customHTML: (datapoints: any[]) => {
          if (!datapoints || datapoints.length === 0) return '';

          const point = datapoints[0];
          const originalData = data.find(d => d.subject === point.key && d.student === point.group);

          return `
          <div class="bx--cc--tooltip">
            <p class="bx--cc--tooltip-total">${point.key}</p>
            <ul class="bx--cc--tooltip-data">
              <li>
                <span class="bx--cc--tooltip-data__label">Student:</span>
                <span class="bx--cc--tooltip-data__value">${point.group}</span>
              </li>
              <li>
                <span class="bx--cc--tooltip-data__label">Current Score:</span>
                <span class="bx--cc--tooltip-data__value">${point.value}%</span>
              </li>
              ${
                originalData?.potential
                  ? `
              <li>
                <span class="bx--cc--tooltip-data__label">Potential:</span>
                <span class="bx--cc--tooltip-data__value">${originalData.potential}%</span>
              </li>`
                  : ''
              }
            </ul>
          </div>
        `;
        },
      },
      legend: {
        enabled: true,
        position: 'top' as const,
        clickable: true,
      },
      animations: {
        enabled: true,
      },
    }),
    [height, data]
  );

  if (loading) {
    return (
      <Tile>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: height + 40,
            gap: 'var(--cds-spacing-03)',
          }}
        >
          <Loading withOverlay={false} />
          <span style={{ color: 'var(--cds-text-secondary)' }}>Loading performance data...</span>
        </div>
      </Tile>
    );
  }

  if (error) {
    return (
      <Tile>
        <InlineNotification title="Chart Error" subtitle={error} kind="error" lowContrast hideCloseButton />
      </Tile>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Tile>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: height + 40,
            color: 'var(--cds-text-secondary)',
          }}
        >
          <ChartRadar size={32} style={{ marginBottom: 'var(--cds-spacing-03)' }} />
          <span>No performance data available</span>
        </div>
      </Tile>
    );
  }

  return (
    <Tile>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--cds-spacing-03)',
          marginBottom: 'var(--cds-spacing-05)',
        }}
      >
        <ChartRadar size={20} style={{ color: 'var(--cds-interactive-01)' }} />
        <h4
          style={{
            fontSize: 'var(--cds-productive-heading-03-font-size)',
            fontWeight: 'var(--cds-productive-heading-03-font-weight)',
            lineHeight: 'var(--cds-productive-heading-03-line-height)',
            letterSpacing: 'var(--cds-productive-heading-03-letter-spacing)',
            margin: 0,
          }}
        >
          {title}
        </h4>
      </div>

      <div
        style={{
          backgroundColor: 'var(--cds-layer-01)',
          borderRadius: 'var(--cds-border-radius)',
          padding: 'var(--cds-spacing-03)',
        }}
      >
        <RadarChart data={chartData} options={chartOptions} />
      </div>
    </Tile>
  );
};

export default SubjectRadarChart;
